import { memo, useEffect, useMemo, useRef } from 'react';

import cn from 'classnames';
import dayjs from 'dayjs';
import {
    ColorType,
    createChart,
    type IChartApi,
    type Time,
} from 'lightweight-charts';

import { THEME } from '@/shared/constants/theme';
import { useLanguage, useTheme } from '@/shared/lib/hooks';
import { Loader } from '@/shared/ui/loader';

import type { TradingPlatformAccountStatsChartData } from '../../models/types/get-account-stats-response';

import cls from './trading-platform-daily-net-cumulative-chart.module.css';

interface TradingPlatformDailyNetCumulativeChartProps {
    chartData: TradingPlatformAccountStatsChartData[];
    isLoading: boolean;
    isMobile?: boolean;
}

export const TradingPlatformDailyNetCumulativeChart = memo(
    (props: TradingPlatformDailyNetCumulativeChartProps) => {
        const { chartData, isLoading, isMobile = false } = props;

        const chartContainerRef = useRef<HTMLDivElement | null>(null);
        const chart = useRef<IChartApi | null>(null);

        const [theme] = useTheme();
        const [language] = useLanguage();

        const initialData = useMemo(() => {
            let accumulatedValue = 0;

            return chartData.map(({ date, total_profit: value }) => {
                accumulatedValue += value;

                return {
                    time: date,
                    value: accumulatedValue,
                };
            });
        }, [chartData]);

        useEffect(() => {
            if (!isLoading) {
                chart.current = createChart(chartContainerRef.current!, {
                    layout: {
                        ...chart.current?.options().layout,
                        fontFamily: "'Roboto', sans-serif",
                        background: {
                            type: ColorType.Solid,
                            color: 'rgba(0, 0, 0, 0)',
                        },
                    },
                    width: chartContainerRef.current!.clientWidth,
                    height: 210,
                    autoSize: true,
                    crosshair: {
                        horzLine: { visible: false },
                        vertLine: { visible: false },
                    },
                    timeScale: {
                        ...chart.current?.options().timeScale,
                        tickMarkFormatter: (time: Time) => time,
                    },
                    grid: {
                        ...chart.current?.options().grid,
                        vertLines: {
                            visible: false,
                        },
                    },
                    handleScroll: true,
                    handleScale: true,
                    leftPriceScale: {
                        ...chart.current?.options().leftPriceScale,
                        visible: true,
                        alignLabels: false,
                    },
                    rightPriceScale: {
                        visible: false,
                    },
                });

                chart.current.timeScale().fitContent();

                const firstItem = initialData[0];
                const isDataStartFromZero = firstItem && !firstItem.value;
                const data = initialData.slice();

                if (firstItem && !isDataStartFromZero) {
                    data.unshift({
                        time: dayjs(firstItem.time)
                            .subtract(1, 'day')
                            .format('YYYY-MM-DD'),
                        value: 0,
                    });
                }

                const baselineSeries = chart.current.addBaselineSeries({
                    baseValue: { type: 'price', price: 0 },
                    topLineColor: 'rgba( 38, 166, 154, 1)',
                    topFillColor1: 'rgba( 38, 166, 154, 0.28)',
                    topFillColor2: 'rgba( 38, 166, 154, 0.05)',
                    bottomLineColor: 'rgba( 239, 83, 80, 1)',
                    bottomFillColor1: 'rgba( 239, 83, 80, 0.05)',
                    bottomFillColor2: 'rgba( 239, 83, 80, 0.28)',
                    priceLineVisible: false,
                    lastValueVisible: false,
                    lineWidth: 1,
                });

                baselineSeries.setData(data);
            }

            return () => {
                if (!isLoading && chart.current) {
                    chart.current.remove();
                }
            };
        }, [initialData, isLoading]);

        useEffect(() => {
            const isDark = theme === THEME.dark;

            chart.current?.applyOptions({
                layout: {
                    background: {
                        type: ColorType.Solid,
                        color: 'transparent',
                    },
                    textColor: isDark
                        ? 'rgb(255 255 255 / 45%)'
                        : 'rgb(0 0 0 / 45%)',
                },
                grid: {
                    horzLines: {
                        color: isDark ? '#2A2E39' : '#F0F0F0',
                    },
                },
                leftPriceScale: {
                    borderColor: isDark ? '#2A2E39' : '#F0F0F0',
                },
                timeScale: {
                    borderColor: isDark ? '#2A2E39' : '#F0F0F0',
                },
            });
        }, [theme]);

        useEffect(() => {
            chart.current?.applyOptions({
                localization: {
                    locale: language,
                },
            });
        }, [language]);

        return (
            <div
                ref={chartContainerRef}
                className={cn(cls.root, { mobile: isMobile })}
            >
                {isLoading && <Loader />}
            </div>
        );
    },
);

TradingPlatformDailyNetCumulativeChart.displayName =
    'TradingPlatformDailyNetCumulativeChart';
